import { SEO } from "@/components/SEO";
import { lazy, type ReactNode, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Hero } from "@/components/Hero";
import { Services } from "@/components/Services";
import { Team } from "@/components/Team";
import { Testimonials } from "@/components/Testimonials";
import { FAQ } from "@/components/FAQ";
import { Footer } from "@/components/Footer";
import { SECTION_NAVIGATION_EVENT } from "@/lib/scrollToSection";

const Portfolio = lazy(() => import("@/components/Portfolio").then((module) => ({ default: module.Portfolio })));
const Contact = lazy(() => import("@/components/Contact").then((module) => ({ default: module.Contact })));

interface DeferredSectionProps {
  children: ReactNode;
  id: string;
  placeholderClassName: string;
  rootMargin?: string;
}

function DeferredContent({ children, onReady }: { children: ReactNode; onReady: () => void }) {
  useEffect(() => {
    onReady();
  }, [onReady]);

  return children;
}

function DeferredSection({
  children,
  id,
  placeholderClassName,
  rootMargin = "700px 0px",
}: DeferredSectionProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [shouldAlign, setShouldAlign] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const markContentReady = useCallback(() => setContentReady(true), []);

  useEffect(() => {
    const element = ref.current;
    if (!element || shouldRender) return;

    if (!("IntersectionObserver" in window)) {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin, shouldRender]);

  useEffect(() => {
    const handleNavigation = (event: Event) => {
      const targetId = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (targetId !== id) return;

      setShouldAlign(true);
      setShouldRender(true);
    };

    window.addEventListener(SECTION_NAVIGATION_EVENT, handleNavigation);
    return () => window.removeEventListener(SECTION_NAVIGATION_EVENT, handleNavigation);
  }, [id]);

  useEffect(() => {
    if (!contentReady || !shouldAlign) return;

    const align = () => ref.current?.scrollIntoView({ behavior: "auto", block: "start" });
    const frame = window.requestAnimationFrame(align);
    const timeout = window.setTimeout(() => {
      align();
      setShouldAlign(false);
    }, 400);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [contentReady, shouldAlign]);

  const placeholder = <div className={placeholderClassName} aria-hidden="true" />;

  return (
    <div id={id} ref={ref}>
      {shouldRender ? (
        <Suspense fallback={placeholder}>
          <DeferredContent onReady={markContentReady}>{children}</DeferredContent>
        </Suspense>
      ) : (
        placeholder
      )}
    </div>
  );
}

export default function Home() {
  return (
    <>
      <SEO />
      <div className="min-h-screen bg-gray-50" data-testid="home-page">
        <Navigation />
        <Hero />
        <main>
          <Services />
          <DeferredSection
            id="portfolio"
            placeholderClassName="min-h-[1520px] md:min-h-[1250px] bg-white"
          >
            <Portfolio sectionId={null} />
          </DeferredSection>
          <Team />
          <Testimonials />
          <FAQ />
          <DeferredSection
            id="contact"
            placeholderClassName="min-h-[1900px] md:min-h-[1100px] gradient-bg"
            rootMargin="1200px 0px"
          >
            <Contact sectionId={null} />
          </DeferredSection>
        </main>
        <Footer />
      </div>
    </>
  );
}
